import { useEffect, useState } from "react";
import {
  Autocomplete,
  TextField,
  Stack,
  Typography,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import StructureMapping, {
  StructureMap,
} from "../components/PlanningAutomation/StructureMapping";
import {
  useAutomationIntegrationApiV1ListTemplatesQuery,
  useAutomationIntegrationApiV1ListTemplateDetailsQuery,
  useAutomationIntegrationApiV1RequestPlanGenerationMutation,
  useWebApiV1OrderQuery,
  OrderStatus,
  useWebApiV1UpdateOrderMutation,
  useAutomationIntegrationApiV1ListMachinesQuery,
  useWebApiV1RelatedPrescriptionsQuery,
  useWebApiV1GetUserFeatureSwitchesQuery,
} from "@providers/hop-ord-server/api";
import { skipToken } from "@reduxjs/toolkit/query/react";
import {
  ChevronLeft,
  Refresh as RefreshIcon,
  AutoFixHigh,
} from "@mui/icons-material";
import {
  StructureStatus,
  OrderStatus as OrderStatusEnum,
  Feature,
} from "@enums";
import useRefreshImportedData from "@utils/useRefreshImportedData";
import { toLocaleDateTimeString } from "@utils";
import { PlanningTemplateBottomBar } from "@pages/OrderTracker/components";

interface Props {
  orderId: number;
}

interface StructureObj {
  name: string;
  type: string;
}

const PlanningTemplateApi = ({ orderId }: Props): JSX.Element => {
  const navigate = useNavigate();

  const [structureState, setStructureState] = useState<StructureMap[]>([]);
  const [selectedTemplate, setSelectedTemplate] = useState("");
  const [relatedPrescription, setRelatedPrescription] = useState("");
  const [relatedPrescriptionId, setRelatedPrescriptionId] = useState("");
  const [selectedMachine, setSelectedMachine] = useState("");
  const [selectedBeamEnergy, setSelectedBeamEnergy] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [showBeamEnergyError, setShowBeamEnergyError] = useState(false);
  const {
    handleRefresh: handleRefreshImportedData,
    lastRefreshedAt,
    refreshStatus,
    setRefreshStatus,
    structuresRetrieved,
  } = useRefreshImportedData(orderId, selectedTemplate);

  const [updateOrder] = useWebApiV1UpdateOrderMutation();

  const { data: featureSwitchData } = useWebApiV1GetUserFeatureSwitchesQuery();
  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  /** List Data query */
  const { data: machines, isLoading: machinesLoading } =
    useAutomationIntegrationApiV1ListMachinesQuery({
      orderId,
    });

  useEffect(() => {
    // Default to the first machine if there is only one
    if (machines?.length === 1 && !selectedMachine) {
      setSelectedMachine(machines[0].name);
    }
  }, [machines]);

  // Get beam energies for selected machine
  const beamEnergyOptions =
    machines?.find((m) => m.name === selectedMachine)?.beam_energies || [];

  // Auto-select beam energy if only one option is available
  useEffect(() => {
    if (beamEnergyOptions.length === 1 && !selectedBeamEnergy) {
      setSelectedBeamEnergy(beamEnergyOptions[0]);
    }
  }, [beamEnergyOptions, selectedBeamEnergy]);

  // Temp options while the list data is not available
  const machineOptions = machines?.map((data) => data.name) || [];

  const { data: order } = useWebApiV1OrderQuery(
    {
      orderId,
    },
    { refetchOnMountOrArgChange: true },
  );

  // Site query (to get names and ID's for related prescriptions)
  const { data: relatedPrescriptions } = useWebApiV1RelatedPrescriptionsQuery({
    orderId: orderId.toString(),
  });

  const rtStructExists = order?.rtstructStatus === StructureStatus.COMPLETE;
  const ctExists = order?.ctStatus === StructureStatus.COMPLETE;

  const showAutomation = rtStructExists && ctExists;

  const warningText =
    !ctExists && !rtStructExists
      ? "Missing CT and RT structure set."
      : !ctExists
        ? "Missing CT."
        : !rtStructExists
          ? "Missing RT structure set."
          : "";

  const { data: templateNames, isLoading: templateNamesLoading } =
    useAutomationIntegrationApiV1ListTemplatesQuery({ orderId });

  const { data: template } =
    useAutomationIntegrationApiV1ListTemplateDetailsQuery(
      selectedTemplate
        ? { orderId, templateName: selectedTemplate }
        : skipToken,
    );

  const [sendPlan] =
    useAutomationIntegrationApiV1RequestPlanGenerationMutation();

  const setOrderStatus = (status: OrderStatus) => {
    return updateOrder({
      orderId,
      orderIn: {
        status,
      },
    });
  };

  const relatedPrescriptionOptions = relatedPrescriptions?.map(
    (prescription) => ({
      label: prescription.relatedPrescriptionName,
      id: prescription.relatedPrescriptionId,
    }),
  );

  const templateOptions = [
    "",
    ...(templateNames?.map(
      (templateName: { name: string }) => templateName.name,
    ) || []),
  ];

  useEffect(() => {
    if (relatedPrescriptions?.length === 1 && !relatedPrescription) {
      // Automatically select the first related prescription and id if there is only one
      setRelatedPrescription(relatedPrescriptions[0].relatedPrescriptionName);
      setRelatedPrescriptionId(
        relatedPrescriptions[0].relatedPrescriptionId.toString(),
      );
    }
  }, [relatedPrescriptions]);

  const scrollToFirstInvalidField = (structures: StructureMap[]) => {
    if (!selectedMachine) {
      const machineElement = document.getElementById("selected-machine");
      machineElement?.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
    } else {
      const firstInvalidStructure = structures.find((s) => !s.isValid);
      const firstInvalidElement = document.getElementById(
        `planning-template-value-${firstInvalidStructure?.id}`,
      );
      firstInvalidElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }
  };

  const runValidation = (structures: StructureMap[]) => {
    const allValid = structures.every((s) => {
      return !(s.mandatory && !s.values.length);
    });

    const updatedStructures = structures.map((structure) => {
      const isInvalid = structure.mandatory && !structure.values.length;
      return {
        ...structure,
        isValid: !isInvalid,
      };
    });

    setStructureState(updatedStructures);
    return (
      allValid &&
      !!selectedMachine &&
      !!selectedBeamEnergy &&
      !!selectedTemplate &&
      !!relatedPrescription
    );
  };

  const submitPlanGeneration = async () => {
    setHasSubmitted(true);
    setShowBeamEnergyError(!!selectedMachine);
    if (!runValidation(structureState)) {
      scrollToFirstInvalidField(structureState);
      return;
    }

    // Submit has been clicked and / or validation has run successfully. Submit form.
    const structureMap: StructureObj[] = [];
    structureState.forEach((s) => {
      if (s.values) {
        const map = s.values.map((value) => ({
          name: s.name,
          type: value,
        }));
        structureMap.push(...map);
      }
    });

    sendPlan({
      orderId,
      planGenerationAttemptIn: {
        template: selectedTemplate,
        structures: structureMap,
        machine: selectedMachine,
        beam_energy: selectedBeamEnergy,
        related_prescription: relatedPrescription,
        related_prescription_id: relatedPrescriptionId,
      },
    }).then(() => {
      setHasSubmitted(true);
      setOrderStatus("sent_to_automation");
      navigate("automated");
    });
  };

  const showCancelButton = order?.status === OrderStatusEnum.SENT_TO_AUTOMATION;

  if (templateNamesLoading || machinesLoading) {
    return (
      <PlanningTemplateBottomBar
        showPlanReview={showPlanReview}
        orderId={orderId}
        showCancelButton={showCancelButton}
      />
    );
  }

  const refreshingImportedData = refreshStatus !== "idle";
  const importedDataLastRefreshed =
    lastRefreshedAt || order?.lastRefreshedImportedDataAt;

  return (
    <Stack width={1} height={1} marginBottom={3} gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" gap={1} paddingTop={1} alignItems="center">
          <Button
            disabled={refreshingImportedData}
            onClick={handleRefreshImportedData}
            startIcon={refreshingImportedData ? null : <RefreshIcon />}
            variant="text"
            data-testid="planning-template-refresh"
          >
            {refreshingImportedData && (
              <CircularProgress
                size={24}
                sx={{ marginRight: "8px", position: "absolute" }}
                color="inherit"
              />
            )}
            <span
              style={{
                visibility: refreshingImportedData ? "hidden" : "inherit",
              }}
            >
              Refresh CT & RTstruct
            </span>
          </Button>
        </Stack>
        {importedDataLastRefreshed && (
          <Typography
            variant="caption"
            color="text.secondary"
            data-testid="last-refreshed-timestamp"
          >
            Last refreshed: {toLocaleDateTimeString(importedDataLastRefreshed)}
          </Typography>
        )}
      </Stack>
      {!showAutomation && (
        <Stack gap={2}>
          <Typography>
            Unable to start planning automation. {warningText}
          </Typography>
          <Button
            variant="outlined"
            sx={{ width: "max-content" }}
            startIcon={<ChevronLeft />}
            onClick={() => navigate("/commandcenter")}
          >
            Back to command center
          </Button>
        </Stack>
      )}
      {showAutomation && (
        <Stack gap={2} width={1}>
          <FormControl
            sx={{ paddingTop: 1, width: "50%" }}
            error={hasSubmitted && !relatedPrescription}
          >
            <Autocomplete
              disableClearable
              options={relatedPrescriptionOptions || []}
              value={{ label: relatedPrescription, id: relatedPrescriptionId }}
              fullWidth
              onChange={(_, newValue: { label: string; id: string } | null) => {
                setRelatedPrescription(newValue?.label || "");
                setRelatedPrescriptionId(newValue?.id || "");
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  error={hasSubmitted && !relatedPrescription}
                  label="Related prescription *"
                />
              )}
            />
            {hasSubmitted && !relatedPrescription && (
              <FormHelperText>This field is required</FormHelperText>
            )}
          </FormControl>
          <Typography variant="subtitle1">Plan Automation</Typography>
          <Stack direction="row" gap={2}>
            <FormControl
              sx={{ width: 1 }}
              error={hasSubmitted && !selectedTemplate}
            >
              <Autocomplete
                options={templateOptions || []}
                value={selectedTemplate}
                fullWidth
                onChange={(_, newValue: string | null) => {
                  setSelectedTemplate(newValue || "");
                  if (refreshStatus === "idle" && newValue) {
                    setRefreshStatus("refetching order");
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={hasSubmitted && !selectedTemplate}
                    label="Automated template *"
                    data-testid="planning-template-select"
                  />
                )}
              />
              {hasSubmitted && !selectedTemplate && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Stack>
          {selectedTemplate && (
            <StructureMapping
              template={template}
              availableStructures={
                refreshingImportedData ? null : structuresRetrieved.structures
              }
              hasSubmitted={hasSubmitted}
              setHasSubmitted={setHasSubmitted}
              setStructureState={setStructureState}
              structureState={structureState}
              runValidation={runValidation}
            />
          )}
          <Typography variant="subtitle1">Plan Configuration</Typography>
          <Stack direction="row" gap={2}>
            <FormControl
              sx={{ width: 1 }}
              error={hasSubmitted && !selectedMachine}
            >
              <InputLabel id="selected-machine-label">Machine *</InputLabel>
              <Select
                id="selected-machine"
                data-testid="selected-machine"
                value={selectedMachine}
                labelId="selected-machine"
                label="Machine *"
                onChange={(e) => {
                  setSelectedMachine(e.target.value);
                  setSelectedBeamEnergy(""); // Reset beam energy when machine changes
                  setShowBeamEnergyError(false); // Reset error state when machine changes
                }}
              >
                {machineOptions.map((machine) => (
                  <MenuItem key={machine} value={machine}>
                    {machine}
                  </MenuItem>
                ))}
              </Select>
              {hasSubmitted && !selectedMachine && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
            <FormControl
              sx={{ width: 1 }}
              error={hasSubmitted && showBeamEnergyError && !selectedBeamEnergy}
              disabled={!selectedMachine}
            >
              <InputLabel id="selected-beam-energy-label">Energy *</InputLabel>
              <Tooltip
                title={
                  !selectedMachine
                    ? "Select a machine first to view the available energy options"
                    : ""
                }
                placement="top"
              >
                <Select
                  id="selected-beam-energy"
                  data-testid="selected-beam-energy"
                  value={selectedBeamEnergy}
                  labelId="selected-beam-energy"
                  label="Energy *"
                  onChange={(e) => setSelectedBeamEnergy(e.target.value)}
                >
                  {beamEnergyOptions.map((energy) => (
                    <MenuItem key={energy} value={energy}>
                      {energy}
                    </MenuItem>
                  ))}
                </Select>
              </Tooltip>
              {hasSubmitted && showBeamEnergyError && !selectedBeamEnergy && (
                <FormHelperText>This field is required</FormHelperText>
              )}
            </FormControl>
          </Stack>
          <Button
            startIcon={<AutoFixHigh />}
            sx={{ width: "max-content" }}
            variant="contained"
            color="primary"
            onClick={() => submitPlanGeneration()}
          >
            Generate Plan
          </Button>
          {(!templateNames?.length || !machineOptions?.length) && (
            <Stack direction="row" gap={1}>
              <Typography color="error">
                No templates and/or machines found.
              </Typography>
            </Stack>
          )}
        </Stack>
      )}

      <PlanningTemplateBottomBar
        showPlanReview={showPlanReview}
        orderId={orderId}
        showCancelButton={showCancelButton}
      />
    </Stack>
  );
};

export default PlanningTemplateApi;
